import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import dayjs from 'dayjs';
import { activityStore } from '../../../../../store/activity.store';
import { ActivityService } from '../../../../../api/activity.service';
import { addHistorySheet, addStatisticsSheet, addRecordsSheet } from './excelHelpers';

const MAX_EXPORT_DAYS = 50;

export interface ExcelExportAlert {
  open: boolean;
  title: string;
  message: string;
}

export const useExcelExport = () => {
  const [isExporting, setIsExporting] = useState(false);
  const [alert, setAlert] = useState<ExcelExportAlert>({ open: false, title: '', message: '' });
  const { formatMessage } = useIntl();

  const closeAlert = () => setAlert({ open: false, title: '', message: '' });

  const statistics = activityStore(state => state.statistics);
  const activityTypes = activityStore(state => state.activityTypes);
  const intervalsParams = activityStore(state => state.intervalsParams);
  const { from, to } = intervalsParams;

  const exportExcel = useCallback(async () => {
    if (!statistics || !activityTypes || !from || !to) {
      console.error('Missing required data for Excel export');
      return;
    }

    const fromDate = dayjs(from);
    const toDate = dayjs(to);
    const daysDiff = toDate.diff(fromDate, 'day') + 1;

    if (daysDiff > MAX_EXPORT_DAYS) {
      setAlert({
        open: true,
        title: formatMessage({ id: 'EXPORT_EXCEL_ERROR_TITLE' }),
        message: formatMessage(
          { id: 'EXPORT_EXCEL_LIMIT_EXCEEDED' },
          { limit: MAX_EXPORT_DAYS, days: daysDiff }
        )
      });
      return;
    }

    setIsExporting(true);

    try {
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const intervalsResponse = await ActivityService.getIntervals(
        { ...intervalsParams, timezone: userTimezone },
        { page: 0, size: MAX_EXPORT_DAYS }
      );
      const daysIntervals = intervalsResponse.data.content;

      if (!daysIntervals || daysIntervals.length === 0) {
        setAlert({
          open: true,
          title: formatMessage({ id: 'EXPORT_EXCEL_ERROR_TITLE' }),
          message: formatMessage({ id: 'EXPORT_EXCEL_NO_DATA' })
        });
        setIsExporting(false);
        return;
      }

      // Dynamic import to avoid bundle bloat
      const ExcelJS = await import('exceljs');
      const wb = new ExcelJS.Workbook();

      addHistorySheet(wb, daysIntervals, activityTypes);
      addStatisticsSheet(wb, statistics);
      addRecordsSheet(wb, daysIntervals, activityTypes);

      // Generate and download
      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `time-report-${from}-to-${to}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Excel export failed:', error);
    } finally {
      setIsExporting(false);
    }
  }, [statistics, activityTypes, intervalsParams, from, to, formatMessage]);

  return { exportExcel, isExporting, alert, closeAlert };
};
